import React, { useRef, useState } from 'react';
import SortableList from '@shared/components/SortableList';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { pxToRem } from '@withjoy/joykit/theme';
import { useThrottledWindowResize } from '@shared/utils/hooks/useThrottledWindowResize';
import { SortableStayZoneTile } from '../SortableStayZoneTile';
import type { StayZoneAreaData } from '@shared/components/StayZoneTile/StayZoneTile.types';
import { useDragSorting } from '@shared/hooks/useDragSorting';
import { StayZoneTile } from '@shared/components/StayZoneTile/StayZoneTile';
import { SkeletonGroup } from '@shared/components/Skeleton/Skeleton';

interface StayZonesListProps
  extends Readonly<{
    loading: boolean;
    stayZones: ReadonlyArray<StayZoneAreaData>;
    onReorder?: (reorderedStayZones: ReadonlyArray<StayZoneAreaData>) => void;
    onView: (stayZone: StayZoneAreaData) => void;
    onRemove: (stayZone: StayZoneAreaData) => void;
  }> {}

export const StayZonesList: React.FC<StayZonesListProps> = ({ loading, stayZones, onReorder, onView, onRemove }) => {
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { sortedItems, isDragActive, handleDragStart, handleDragCancel, handleDragEnd } = useDragSorting({
    items: stayZones,
    onReorder,
  });

  // Measure container width for consistent drag overlay sizing
  const measureWidth = useEventCallback(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  });

  useThrottledWindowResize(measureWidth);

  return (
    <SkeletonGroup
      rootRef={containerRef}
      isReady={!loading}
      placeholder={<StayZoneTile.Skeleton featureCount={3} />}
      display="flex"
      flexDirection="column"
      gap={6}
      marginX={6}
      marginTop={5}
      paddingBottom={pxToRem(100)}
    >
      <SortableList
        items={Array.from(sortedItems)}
        renderItem={(stayZone, index, isItemDragActive) => {
          return (
            <SortableStayZoneTile
              key={`stay-zone-tile-${stayZone.id}-${index}`}
              stayZone={stayZone}
              isDragActive={isDragActive}
              onView={onView}
              onRemove={onRemove}
              containerWidth={containerWidth}
              isItemDragActive={isItemDragActive}
            />
          );
        }}
        onChange={() => {}}
        hasDragHandle={true}
        handleDragStart={handleDragStart}
        handleDragEnd={handleDragEnd}
        handleDragCancel={handleDragCancel}
        adjustOverlayScale={false}
      />
    </SkeletonGroup>
  );
};
